import { inject, Injectable, ViewContainerRef } from "@angular/core";
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { distinctUntilChanged, map } from "rxjs/operators";

import components from "../components/component-map";
import { FormGroupComponent } from "../components/form-group/form-group.component";
import { DynamicFormComponent } from "../containers/dynamic-form/dynamic-form.component";
import { DFormControl } from "../entities/form-control";
import { FormField } from "../entities/form-field";
import { DFormGroup } from "../entities/form-group";
import { I18nPipe } from "../pipes/i18n.pipe";
import { FormFieldFactory } from "../services/form-field.factory";
import { COMPONENT_MAP } from "../tokens";
import * as utils from "../utils/utils";

@Injectable()
export class DynamicFormsFactory {
	private componentMap = inject<Map<string, any>>(COMPONENT_MAP);
	private i18nPipe = inject(I18nPipe);
	private formFieldFactory = inject(FormFieldFactory);
	private fb = inject(UntypedFormBuilder);

	private validator(componentFormGroup: UntypedFormGroup) {
		return () => {
			return componentFormGroup.valid
				? null
				: {
						invalidCustomForm: true,
					};
		};
	}

	private createCustomComponent(container: ViewContainerRef, config: DFormControl, group: UntypedFormGroup) {
		const control = this.formFieldFactory.getCustomControl(config);

		if (!control) {
			console.warn(`No component found for type \`${config.type}\``);

			return;
		}

		const originalControl = group.get(config.name);
		group.removeControl(config.name);

		const componentFormGroup = this.fb.group(originalControl.value || {}, { validator: this.validator });

		if (control["@output"]) {
			let outputControl: AbstractControl;

			// If we have an `@output` projection, we listen to the changes in the form of the component and project this via the `@output` property.
			componentFormGroup.valueChanges
				.pipe(
					map((value) => {
						if (typeof control["@output"] === "string") {
							return utils.interpolateValue(control["@output"], value);
						}

						return utils.interpolate({ ...(control["@output"] as any) }, value);
					}),
					distinctUntilChanged(),
				)
				.subscribe((value) => {
					if (!outputControl) {
						if (value !== null) {
							outputControl = this.fb.group(value);
						} else {
							outputControl = this.fb.control({});
						}

						outputControl.setValidators(this.validator(componentFormGroup));

						if (originalControl.disabled) {
							outputControl.disable();
						}

						group.addControl(config.name, outputControl);

						// Only set the component form group value when first rendering
						componentFormGroup.patchValue(utils.recover(control["@output"], originalControl.value));
					}

					outputControl.patchValue(value);
				});
		} else {
			group.addControl(config.name, componentFormGroup);
		}

		const cmp = container.createComponent(DynamicFormComponent);
		cmp.instance.config = control.definition;
		cmp.instance.group = componentFormGroup;
		cmp.instance.disabled = originalControl.disabled;

		return cmp;
	}

	createComponent(container: ViewContainerRef, config: DFormControl, group: UntypedFormGroup) {
		// Retrieve the components from the custom component map first
		let component = this.componentMap.get(config.type) || components.get(config.type);

		if (!component) {
			const customComponent = this.createCustomComponent(container, config, group);

			if (!customComponent) {
				component = this.componentMap.get("*");

				if (!component) {
					return;
				}
			} else {
				return customComponent;
			}
		}

		const projections = [];

		if (config.label) {
			const label = document.createElement("h3");
			label.appendChild(document.createTextNode(this.i18nPipe.transform(config.label)));

			projections.push(label);
		}

		const cmp = container.createComponent<FormField>(component, {
			projectableNodes: projections.map((projection) => [projection]),
		});
		cmp.instance.config = config;
		cmp.instance.group = group;

		return cmp;
	}

	createGroup(container: ViewContainerRef, config: DFormGroup, group: UntypedFormGroup) {
		const cmp = container.createComponent(FormGroupComponent);
		cmp.instance.config = config;
		cmp.instance.group = group.get(config.name) as UntypedFormGroup;

		return cmp;
	}
}
